import { useCallback, useEffect, useState } from "react";
import { Loader2, RadioTower, RefreshCw } from "lucide-react";
import {
  fetchHermesGrokStatus,
  rescanHermesGrokStatus,
  type HermesGrokStatus,
} from "../../lib/hermes-grok";
import { SettingsActionStatus } from "./SettingsActionStatus";
import { isElectron } from "../../lib/platform";

export function GrokHermesRuntimeStatus() {
  const [status, setStatus] = useState<HermesGrokStatus | null>(null);
  const [isBusy, setIsBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const isDesktop = isElectron();

  const refresh = useCallback(async () => {
    if (!isDesktop) {
      setStatus(null);
      try {
        localStorage.setItem("fintheon:grok-oauth-status", "missing");
      } catch {
        /* ignore */
      }
      return;
    }
    const nextStatus = await fetchHermesGrokStatus().catch(() => null);
    setStatus(nextStatus);
    try {
      localStorage.setItem(
        "fintheon:grok-oauth-status",
        nextStatus?.available ? "set" : "missing",
      );
      localStorage.setItem(
        "fintheon:hermes-set-model-status",
        nextStatus ? "set" : "missing",
      );
    } catch {
      /* ignore */
    }
  }, [isDesktop]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  async function connectLocalGrok() {
    setIsBusy(true);
    setMessage(null);
    try {
      if (isElectron() && window.electron?.runShellCommand) {
        await window.electron.runShellCommand("hermes model");
        setMessage("Hermes model picker opened.");
      } else {
        setMessage("Connect Grok from the Fintheon desktop app.");
        return;
      }
      const next = await rescanHermesGrokStatus();
      setStatus(next);
      await refresh();
    } catch {
      setMessage("Grok connect failed.");
    } finally {
      setIsBusy(false);
    }
  }

  const isConnected = isDesktop && status?.available;
  const runtimeDetail = isDesktop
    ? (status?.detail ?? "Run hermes model to connect xai-oauth.")
    : "Desktop-only. Mobile and web use your DeepSeek API key.";

  return (
    <div className="fintheon-fade-divider py-3 text-right">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 text-right">
          <p className="text-[11px] font-medium text-zinc-300">
            Grok via Hermes
          </p>
          <p className="mt-0.5 text-[10px] leading-snug text-zinc-600">
            {runtimeDetail}
          </p>
        </div>
        <div className="flex min-w-[150px] flex-col items-end gap-2">
          <SettingsActionStatus
            label={isConnected ? "Connected" : "Connect"}
            detail={isDesktop ? (status?.model ?? "xai-oauth") : "Desktop only"}
            tone={isConnected ? "success" : "warning"}
          />
          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={connectLocalGrok}
              disabled={isBusy || !isDesktop}
              className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.14em] text-[var(--fintheon-accent)] transition-colors disabled:opacity-40"
            >
              {isBusy ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <RadioTower className="h-3 w-3" />
              )}
              {isDesktop ? "Connect" : "Desktop only"}
            </button>
            <button
              type="button"
              onClick={refresh}
              className="text-zinc-500 transition-colors hover:text-zinc-300"
              title="Refresh Grok status"
            >
              <RefreshCw className="h-3 w-3" />
            </button>
          </div>
        </div>
      </div>
      {message && (
        <p className="mt-2 text-right text-[10px] text-zinc-500">{message}</p>
      )}
    </div>
  );
}
